<script lang="ts" setup>
import { ref, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { formatDate, deepParseDates } from '@/utils/date-utils';
import { useQuery, useMutation } from '@vue/apollo-composable';
import { useUserStore } from '@/store/user';
import {
  ProjectsDocument,
  ProjectCreateDocument,
  ProjectType,
} from '@/gql/graphql';
import {
  BAlert,
  BButton,
  BCard,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BModal,
  BPagination,
  BTable,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { PAGING_ITEMS_PER_PAGE, TRUNCATE_TEXT_LENGTH } from '@/constants';
import { truncateText } from '@/utils/string-utils';
import ProjectStatus from '@/components/project/ProjectStatus.vue';
import { openRouterLink } from '@/utils/router-utils';

const userStore = useUserStore();
const router = useRouter();

// New Project Form
const newProjectName = ref<string>('');
const newProjectDescription = ref<string>('');
const newProjectModalShow = ref(false);

// Filtering and paging state
const namePattern = ref<string | null>(null);
const descriptionPattern = ref<string | null>(null);
const userRole = ref<string | null>(null);

const pagingPageCurrent = ref(1);
const pagingItemsPerPage = ref(PAGING_ITEMS_PER_PAGE);
const pagingTotalRows = computed(() => {
  return projectsQueryResult.value?.projects?.total || 0;
});

function clearFilter() {
  namePattern.value = null;
  descriptionPattern.value = null;
  userRole.value = null;
  pagingPageCurrent.value = 1;

  requestRefetchProjects();
}

// --- User Projects ---
const projects = computed<ProjectType[]>(() => {
  return (
    projectsQueryResult.value?.projects?.projects
      ?.filter((prj) => prj && prj !== null)
      .map((prj) => ({
        ...(deepParseDates(prj) as ProjectType),
      })) || []
  );
});

// GraphQL Query to Get Projects
const {
  result: projectsQueryResult,
  loading: projectsQueryInProgress,
  error: projectsQueryError,
  refetch: refetchProjects,
} = useQuery(ProjectsDocument, {
  namePattern: namePattern.value,
  descriptionPattern: descriptionPattern.value,
  userRole: userRole.value,
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: pagingItemsPerPage.value,
});

// Function to refetch data
function requestRefetchProjects() {
  refetchProjects({
    namePattern: namePattern.value,
    descriptionPattern: descriptionPattern.value,
    userRole: userRole.value,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: pagingItemsPerPage.value,
  })
    ?.catch((err) => {
      console.error('Refetch failed:', err);
    })
    .then(() => {
      console.log('Refetched');
    });
}

// GraphQL Mutation to Create Project
const {
  mutate: createProject,
  onDone: onCreateProjectDone,
  onError: onCreateProjectError,
} = useMutation(ProjectCreateDocument);

// Loading and Error States
const loadingCreateProject = ref(false);
const showCreateProjectSuccess = ref(false);
const errorCreateProject = ref<string | null>(null);

// Function to submit new project
async function onSubmitNewProject() {
  if (!newProjectName.value) {
    errorCreateProject.value = 'Project name is required.';
    return;
  }
  errorCreateProject.value = null;
  loadingCreateProject.value = true;
  createProject({
    name: newProjectName.value,
    description: newProjectDescription.value,
  });
}

// Handle the result of the createProject mutation
onCreateProjectDone((res) => {
  if (res.data && res.data.projectCreate) {
    loadingCreateProject.value = false;
    showCreateProjectSuccess.value = true;
    newProjectName.value = '';
    setTimeout(() => {
      showCreateProjectSuccess.value = false;
    }, 2000);
    requestRefetchProjects();
    // TODO: Update Cache, insert new item manually ?
    router.push({
      name: 'ProjectDetail',
      params: { id: res.data.projectCreate.id },
    });
  } else {
    console.error('Project creation failed:', res.data);
    loadingCreateProject.value = false;
    errorCreateProject.value = 'Failed to create project. Please try again.';
  }
});

// Handle errors in the createProject mutation
onCreateProjectError((err) => {
  console.error('Error creating project:', err);
  loadingCreateProject.value = false;
  errorCreateProject.value = 'Failed to create project. Please try again.';
});

// Table Fields
const fields = [
  {
    key: 'createdAt',
    label: 'Created',
    sortable: true,
    formatter: formatDate,
    tdClass: 'align-middle',
  },
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'description',
    label: 'Description',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'status',
    label: 'Status',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'role',
    label: 'Role',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'actions',
    label: '',
    tdClass: 'align-middle text-end',
  },
];

// --- Handlers ---
function onSearchRequested() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1;
  } else {
    requestRefetchProjects();
  }
}

function getProjectUserRole(project: ProjectType): string {
  return (
    project.usersAssociated?.find(
      (assoc) =>
        assoc &&
        assoc !== null &&
        ((assoc.user && assoc.user.id === userStore.id) ||
          (assoc.userId && assoc.userId === userStore.id))
    )?.userRole || '-'
  );
}

function goToProjectDetail(
  project: ProjectType,
  index?: number,
  evt?: MouseEvent
) {
  openRouterLink(
    {
      name: 'ProjectDetail',
      params: { id: project.id },
      query: {
        returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
        returnLabel: 'Projects',
      },
    },
    router,
    evt
  );
}

function closeNewProjectModal() {
  newProjectModalShow.value = false;
  newProjectName.value = '';
  newProjectDescription.value = '';
  errorCreateProject.value = null;
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchProjects();
});
</script>

<template>
  <div>
    <div v-if="userStore.noOrganizationSelected && !projectsQueryInProgress">
      <!-- No organization - Cannot Create Project -->
      <b-alert v-model="userStore.noOrganizationSelected" variant="warning">
        You are not associated with any organization. Please contact your
        account manager or organization administrator to get access to the
        upload features.
      </b-alert>
    </div>
    <!-- New Project Form (optional, based on props) -->
    <div v-else-if="userStore.activeOrgAssociation" class="my-3">
      <b-button
        type="submit"
        variant="outline-dark"
        class="flex-shrink-0"
        @click="newProjectModalShow = true"
        :disabled="loadingCreateProject"
      >
        <font-awesome-icon :icon="['fa', 'plus']" />
        Create Project
      </b-button>
    </div>

    <!-- Project List Overlay -->
    <b-alert
      v-model="projectsQueryError"
      variant="danger"
      dismissible
      class="mt-3"
    >
      {{ projectsQueryError }}
      <b-button
        @click="requestRefetchProjects"
        variant="outline-dark"
        class="btn-sm"
      >
        <font-awesome-icon :icon="['fa', 'sync']" />
        Retry</b-button
      >
    </b-alert>

    <!-- Filter -->
    <div class="my-2">
      <BForm
        class="d-flex flex-row align-items-end flex-wrap gap-2"
        @submit.prevent="onSearchRequested"
      >
        <BFormGroup label="Project name" label-for="project-name">
          <BFormInput
            id="project-name"
            type="text"
            v-model="namePattern"
            placeholder="e.g. My Project"
          />
        </BFormGroup>
        <BFormGroup label="Description" label-for="description-pattern">
          <BFormInput
            id="description-pattern"
            type="text"
            v-model="descriptionPattern"
            placeholder="e.g. CD34+"
          />
        </BFormGroup>
        <BFormGroup label="Role" label-for="role">
          <BFormInput
            id="role"
            type="text"
            v-model="userRole"
            placeholder="e.g. owner"
          />
        </BFormGroup>
        <!-- Filter action buttons -->
        <BButton variant="outline-secondary" @click="clearFilter">
          <font-awesome-icon icon="trash" class="me-1" />
          Clear filter
        </BButton>
        <BButton
          variant="outline-dark"
          type="submit"
          :disabled="projectsQueryInProgress"
        >
          <span v-if="projectsQueryInProgress">
            <b-spinner small label=""></b-spinner>
            Searching
          </span>
          <span v-else>
            <font-awesome-icon icon="magnifying-glass" class="me-1" />
            Refresh
          </span>
        </BButton>
      </BForm>
    </div>

    <!-- Paging (above) -->
    <BPagination
      v-if="pagingTotalRows > pagingItemsPerPage"
      v-model="pagingPageCurrent"
      :total-rows="pagingTotalRows"
      :per-page="pagingItemsPerPage"
      pills
      size="sm"
    />

    <!-- List of Projects -->
    <b-card v-if="!projectsQueryError" class="my-3">
      <b-table
        striped
        responsive
        hover
        show-empty
        class="browser-table"
        :items="projects"
        :fields="fields"
        :busy="projectsQueryInProgress"
        @row-clicked="goToProjectDetail"
      >
        <!-- Table busy -->
        <template #table-busy>
          <div
            class="d-flex align-items-center justify-content-center"
            style="min-height: 200px"
          >
            <b-spinner small class="align-middle me-1"></b-spinner>
            <span class="ms-1">Loading projects...</span>
          </div>
        </template>

        <!-- table empty -->
        <template #empty>
          <div class="text-center my-5">
            <p class="text-muted">No projects found.</p>
          </div>
        </template>

        <!-- Table content -->
        <!-- Default data cell scoped slot -->
        <template #cell(name)="data">
          <strong>{{ data.value }}</strong>
        </template>
        <!-- Project description -->
        <template #cell(description)="data">
          <span :id="'project-description-' + data.item.id">
            {{ truncateText(data.value, TRUNCATE_TEXT_LENGTH) }}
          </span>
          <b-tooltip
            v-if="data.value && data.value.length > TRUNCATE_TEXT_LENGTH"
            :target="'project-description-' + data.item.id"
            class="large-tooltip"
          >
            {{ data.value }}
          </b-tooltip>
        </template>
        <!-- Status -->
        <template #cell(status)="data">
          <ProjectStatus :project="data.item" />
        </template>
        <!-- User role -->
        <template #cell(role)="data">
          {{ getProjectUserRole(data.item) }}
        </template>
        <!-- Project detail button -->
        <template #cell(actions)="data">
          <b-button
            variant="outline-dark"
            class="btn-sm"
            @click="goToProjectDetail(data.item, data.item.id, $event)"
            @click.stop
          >
            <font-awesome-icon :icon="['fa', 'eye']" />
          </b-button>
        </template>
      </b-table>
    </b-card>

    <!-- Paging (bellow) -->
    <BPagination
      v-if="pagingTotalRows > pagingItemsPerPage"
      v-model="pagingPageCurrent"
      :total-rows="pagingTotalRows"
      :per-page="pagingItemsPerPage"
      pills
      size="sm"
    />

    <!-- Create New Project Modal -->
    <div v-if="newProjectModalShow">
      <b-modal
        v-model="newProjectModalShow"
        id="create-project-modal"
        @hide="closeNewProjectModal"
      >
        <!-- Title -->
        <template #title>
          <font-awesome-icon :icon="['fa', 'diagram-project']" class="me-2" />
          Create New Project
        </template>

        <!-- Content -->
        <div>
          <b-form-group>
            <label for="project-name">
              <font-awesome-icon :icon="['fa', 'tag']" class="me-1" />
              Project Name
            </label>
            <b-form-input
              id="project-name"
              v-model="newProjectName"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group class="mt-3">
            <label for="project-description">
              <font-awesome-icon :icon="['fa', 'file-lines']" class="me-1" />
              Description
            </label>
            <b-form-textarea
              id="project-description"
              v-model="newProjectDescription"
              rows="3"
            ></b-form-textarea>
          </b-form-group>

          <!-- Error Message -->
          <b-alert
            v-model="errorCreateProject"
            variant="danger"
            dismissible
            class="mt-3"
            @dismissed="errorCreateProject = null"
          >
            {{ errorCreateProject }}
          </b-alert>
        </div>

        <!-- Modal footer with action buttons -->
        <template #footer>
          <BButton
            variant="secondary"
            @click="closeNewProjectModal"
            :disabled="loadingCreateProject"
            >Cancel</BButton
          >
          <BButton
            variant="primary"
            :disabled="loadingCreateProject"
            @click="onSubmitNewProject"
          >
            <font-awesome-icon :icon="['fa', 'plus']" class="me-1" />
            <span v-if="loadingCreateProject">
              <b-spinner small label=""></b-spinner>
              Creating Project
            </span>
            <span v-else-if="showCreateProjectSuccess">
              Project Created
              <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
            </span>
            <span v-else> Create Project </span>
          </BButton>
        </template>
      </b-modal>
    </div>
  </div>
</template>

<style scoped></style>
